import React, { type PropsWithChildren, useMemo } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import UFOCustomData from '@atlaskit/react-ufo/custom-data';
import type { selectedIssue_issueNavigator$key as IssuesFragment } from '@atlassian/jira-relay/src/__generated__/selectedIssue_issueNavigator.graphql';
import { toIssueKey, type IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { convertToView } from '../../common/utils/index.tsx';
import { SelectedIssueContainer } from '../../controllers/selected-issue/index.tsx';
import { useSelectedViewState } from '../../controllers/selected-view-state/index.tsx';
import { useSearchKey } from '../../services/issue-search/selectors.tsx';
import { useFlatListIssues } from '../../controllers/flat-list-issues/index.tsx';

type Props = {
	issues: IssuesFragment | null;
	/** Issue key that should be selected.  */
	selectedIssueKey: IssueKey;
	/** Event called when the selected issue key is changed. */
	onChange: (issueKey: IssueKey, isSelectedByUserInteraction: boolean) => void;
};

export const SelectedIssue = ({
	children,
	issues,
	selectedIssueKey,
	onChange,
}: PropsWithChildren<Props>) => {
	const [{ view }] = useSelectedViewState();
	const searchKey = useSearchKey();
	const issueData = useFragment<IssuesFragment>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment selectedIssue_issueNavigator on JiraIssueConnection {
				edges {
					node {
						key
					}
				}
			}
		`,
		issues,
	);

	let issuesFlatList: string[] | undefined;
	if (fg('jsc_m2_hierarchy_fe_changes')) {
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/rules-of-hooks
		issuesFlatList = useFlatListIssues();
	}

	const issueKeys = useMemo(
		() =>
			view !== 'detail' && issuesFlatList && fg('jsc_m2_hierarchy_fe_changes')
				? issuesFlatList
				: (issueData?.edges?.map((edge) => toIssueKey(edge?.node?.key ?? '')) ?? []),
		[issuesFlatList, issueData?.edges, view],
	);

	const ufoData = useMemo(
		() =>
			fg('nin_perf_extra_ufo_attributes')
				? {
						issuesLoaded: issueKeys.length,
					}
				: undefined,
		[issueKeys.length],
	);

	return (
		<SelectedIssueContainer
			view={convertToView(view)}
			issueKeys={issueKeys}
			searchKey={searchKey}
			selectedIssueKey={selectedIssueKey}
			onChange={onChange}
		>
			{children}
			{ufoData && fg('nin_perf_extra_ufo_attributes') && <UFOCustomData data={ufoData} />}
		</SelectedIssueContainer>
	);
};
